import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Box,
  Divider,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from "@material-ui/core";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockIcon from "@mui/icons-material/Lock";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { hexToCSSFilter } from "hex-to-css-filter";
import defaultimg from "../../assets/images/default.png";
import VegIcon from "../../assets/images/Veg Icon 1.svg";
import NonVegIcon from "../../assets/images/Non Veg Icon 1.svg";
import couponWebSvg from "../../assets/web/coupon.svg";
import { BUTTON_COLOR } from "../../utils/constant";
import {
  addQuantity,
  subtractQuantity,
  specialRequest,
  addDiscount,
  removeDiscount,
  updateItemSpecialRequest,
} from "../../store/actions/cartActions";
import { set_order_type, applied_offer, clear_applied_offer } from "../../store/actions/action";
import { computeCheckoutTotals } from "../../utils/order_helper";
import { routeOfferThroughEngine } from "../../utils/offer_router";
import { getFSRFlow, stripTablePrefix } from "../../utils/fsr_helper";
import OrderTypePopup from "../../components/OrderTypePopup";
import TentPopup from "../../components/TentPopup";
import AddressPopup from "../login/AddressPopup";

// Checks if an offer is currently active based on:
// - offer_header.effective_date / end_date (date range)
// - offer_scheduler.days[].day + time_frame (day/time slots)
// If no offer_scheduler is set, the offer is always visible.
const isOfferActiveByScheduler = (offer, storeInfo, storeInfoConfig) => {
  if (!offer) return false;

  // Date range check using offer_header.effective_date and end_date
  const now = new Date();
  const effectiveDate = offer?.offer_header?.effective_date;
  const endDate = offer?.offer_header?.end_date;
  if (effectiveDate && new Date(effectiveDate) > now) return false;
  if (endDate && new Date(endDate) < now) return false;

  // If no scheduler configured, offer is always active
  const scheduler = offer?.offer_scheduler;
  if (!scheduler || !Array.isArray(scheduler.days) || scheduler.days.length === 0) return true;

  // is_all_days: true means no day/time restriction
  if (scheduler.is_all_days === true) return true;

  // Get store timezone
  const timeZone =
    storeInfo?.store_timezone ||
    storeInfoConfig?.store_timezone ||
    storeInfoConfig?.general_config?.store_timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Current day as short name ("Mon", "Tue", ...) in store timezone
  const currentDay = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(now);

  // Current time as "HH:mm" in store timezone
  const timeParts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit", minute: "2-digit", hour12: false, hourCycle: "h23", timeZone,
  }).formatToParts(now);
  let hour = timeParts.find((p) => p.type === "hour")?.value || "00";
  if (hour === "24") hour = "00";
  const minute = (timeParts.find((p) => p.type === "minute")?.value || "00").padStart(2, "0");
  const currentTime = `${hour.padStart(2, "0")}:${minute}`;

  // Find the matching day entry
  const matchingDay = scheduler.days.find(
    (d) => (d?.day || "").toLowerCase() === currentDay.toLowerCase()
  );
  if (!matchingDay) return false;

  // No time_frame means whole day is active
  const timeFrames = matchingDay.time_frame;
  if (!timeFrames || !Array.isArray(timeFrames) || timeFrames.length === 0) return true;

  // Check if current time falls within any time_frame slot
  return timeFrames.some((tf) => {
    const start = tf?.start_time || "00:00";
    const end   = tf?.end_time   || "23:59";
    return currentTime >= start && currentTime <= end;
  });
};

export default function DesktopCartView(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartProps = useSelector((state) => state.cartReducer) || {};
  const sessionReducer = useSelector((state) => state.sessionReducer) || {};
  const customerReducer = useSelector((state) => state.customerReducer) || {};

  const { addedItems, subTotal, total_tax, total, specialRequestText, orderType, resultDis } = cartProps;
  const orderLevelNote = specialRequestText || cartProps?.comment || "";
  const storeInfo = sessionReducer?.store_details || {};
  const storeInfoConfig = sessionReducer?.store_config || {};
  const is_fsr = sessionReducer?.is_fsr === true;
  const storeOrderTypes = sessionReducer?.order_types || [];

  let primaryColor = storeInfoConfig?.general_config?.primary_color || BUTTON_COLOR;

  const primaryFilter = useMemo(() => {
    try {
      if (!primaryColor) return "";
      const res = hexToCSSFilter(primaryColor);
      return res?.filter ? `brightness(0) saturate(100%) ${res.filter}` : "";
    } catch (e) {
      return "";
    }
  }, [primaryColor]);

  let storeCurrency = storeInfo?.store_currency;

  const currency = useMemo(() => {
    switch (storeCurrency) {
      case "INR": return "₹";
      case "USD": return "$";
      case "CAD": return "$";
      case "OMR": return "OMR ";
      case "AED": return "AED ";
      default: return "₹";
    }
  }, [storeCurrency]);

  const [optInOffers, setOptInOffers] = useState(true);
  const [openInstructionsDialog, setOpenInstructionsDialog] = useState(false);
  const [instructionsText, setInstructionsText] = useState(orderLevelNote);
  const [orderTypeAnchor, setOrderTypeAnchor] = useState(null);
  const [openOrderTypePopup, setOpenOrderTypePopup] = useState(false);
  const [pendingOrderType, setPendingOrderType] = useState(null);
  const [openTent, setOpenTent] = useState(false);

  const [openItemNoteDialog, setOpenItemNoteDialog] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [itemNoteText, setItemNoteText] = useState("");

  const handleOpenItemNote = (item) => {
    setEditingCartItem(item);
    setItemNoteText(item?.specialRequest || item?.special_request || item?.instruction || "");
    setOpenItemNoteDialog(true);
  };

  const handleSaveItemNote = () => {
    if (editingCartItem) {
      if (props.updateItemSpecialRequest) {
        props.updateItemSpecialRequest(editingCartItem.itemUniqueId, itemNoteText);
      } else {
        dispatch(updateItemSpecialRequest(editingCartItem.itemUniqueId, itemNoteText));
      }
    }
    setOpenItemNoteDialog(false);
    setEditingCartItem(null);
    setItemNoteText("");
  };

  useEffect(() => {
    setInstructionsText(orderLevelNote);
  }, [orderLevelNote]);

  // Engine Totals calculation - exact same calculation engine as mobile
  const engineTotals = useMemo(() => {
    if (!addedItems || addedItems.length === 0) return null;
    try {
      return computeCheckoutTotals(addedItems, {
        orderType: orderType?.order?.order_type,
        storeId: storeInfo?.store_id,
        paymentOption: cartProps?.selectedTender,
        storeConfig: storeInfoConfig,
        tip: cartProps?.tip || 0,
        applyCurrentOffer: true,
      });
    } catch (e) {
      return null;
    }
    // engineDiscount is read via store.getState() inside computeCheckoutTotals
    // (applyCurrentOffer:true) — declare it here so the memo re-runs on offer
    // apply/remove.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedItems, cartProps?.tip, cartProps?.selectedTender, orderType, storeInfo?.store_id, storeInfoConfig, cartProps?.engineDiscount]);

  // Sync engine totals into Redux store so entire app state stays in sync
  useEffect(() => {
    if (engineTotals) {
      dispatch({ type: "SYNC_CART_TOTALS", payload: engineTotals });
    }
  }, [dispatch, engineTotals]);

  // All display values come from the engine (offer replay + tip fold-in are
  // handled inside computeCheckoutTotals via applyCurrentOffer:true).
  const displayItemTotal = engineTotals?.sub_total ?? 0;
  const packagingCharges = engineTotals?.total_packing_charge ?? engineTotals?.packaging_charge ?? 0;
  const displayTax = engineTotals?.total_item_tax ?? 0;
  const discountAmount = engineTotals?.total_discount ?? 0;
  const grandTotal = engineTotals?.transaction_grand_amount ?? 0;

  const getTableDisplayName = () => {
    const table = getFSRFlow() || sessionReducer?.table_details;
    const tentId = sessionReducer?.tent_id;
    const name = stripTablePrefix(table?.table_no || table?.table_name || tentId || "");
    if (name) {
      return ` (${name})`;
    }
    return "";
  };

  const activeOrderTypeName = useMemo(() => {
    if (is_fsr) return `Dine In${getTableDisplayName()}`;
    const code = orderType?.order?.order_code;
    if (code === "DI" || code === "di") return `Dine In${getTableDisplayName()}`;
    if (code === "TA" || code === "ta") return "Takeaway";
    if (code === "DEL" || code === "del") return "Delivery";
    return orderType?.order?.order_type || "Takeaway";
  }, [is_fsr, orderType, sessionReducer?.table_details, sessionReducer?.tent_id]);

  const customer = customerReducer?.customer || {};
  const selectedAddressText = customer?.cust_address || (customer?.address?.length ? customer.address[0]?.cust_address : "");
  const orderCode = orderType?.order?.order_code;
  const isDelivery = orderCode === "DEL" || orderCode === "del" || orderType?.order?.order_type?.toLowerCase() === "delivery";

  const handleSaveInstructions = () => {
    dispatch(specialRequest(instructionsText));
    setOpenInstructionsDialog(false);
  };

  // Offers from store config / cartProps, filtered by scheduler date/day/time
  const storeOffers = useMemo(() => {
    const rawOffers = cartProps?.storeOffer || storeInfoConfig?.store_offers || [];
    return rawOffers.filter((offer) => isOfferActiveByScheduler(offer, storeInfo, storeInfoConfig));
  }, [cartProps?.storeOffer, storeInfoConfig, storeInfo]);

  const appliedOffer = cartProps?.appliedOffer;

  const getOfferId = (o) => {
    if (!o) return null;
    if (Array.isArray(o)) return getOfferId(o[0]);
    return o._id || o.offer_id || o.id || null;
  };

  const currentAppliedOfferId = getOfferId(appliedOffer) || cartProps?.engineDiscount?.offer_id || null;
  const isOfferApplied = Boolean(currentAppliedOfferId || discountAmount > 0);

  const [couponInputText, setCouponInputText] = useState("");
  const [couponError, setCouponError] = useState("");

  const visibleStoreOffers = useMemo(() => {
    const enteredCode = (couponInputText || "").trim().toLowerCase();

    if (enteredCode !== "") {
      // While searching: ONLY show offer(s) matching the exact promo code
      return storeOffers.filter((offer) => {
        const promoCodes = [
          offer?.promo_code,
          offer?.code,
          ...(offer?.offer_term || []).map((t) => t?.coupon_code),
        ]
          .filter(Boolean)
          .map((c) => String(c).trim().toLowerCase());

        return promoCodes.some((c) => c === enteredCode);
      });
    }

    // When input is empty: hide any offer that has a coupon/promo code,
    // UNLESS that discount is currently applied.
    return storeOffers.filter((offer) => {
      const targetOfferId = getOfferId(offer);
      const isThisOfferApplied = isOfferApplied && Boolean(
        currentAppliedOfferId && targetOfferId && String(currentAppliedOfferId) === String(targetOfferId)
      );

      // If the discount is currently applied, keep it listed so it can be viewed and removed
      if (isThisOfferApplied) return true;

      const promoCodes = [
        offer?.promo_code,
        offer?.code,
        ...(offer?.offer_term || []).map((t) => t?.coupon_code),
      ]
        .filter(Boolean)
        .map((c) => String(c).trim().toLowerCase());

      return promoCodes.length === 0;
    });
  }, [storeOffers, couponInputText, currentAppliedOfferId, isOfferApplied]);

  const handleApplyOffer = async (offer) => {
    const targetOfferId = getOfferId(offer);
    const isThisOfferApplied = isOfferApplied && Boolean(currentAppliedOfferId && targetOfferId && String(currentAppliedOfferId) === String(targetOfferId));

    dispatch(removeDiscount());
    dispatch(clear_applied_offer());
    dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });

    if (!isThisOfferApplied) {
      const termStyle = offer?.offer_header?.term_style;
      const offerToApply = offer;

      dispatch(applied_offer(offerToApply));
      dispatch(addDiscount(offerToApply.offer_term, termStyle));
      const engineCtx = {
        orderType: orderType?.order?.order_type || "Dine In",
        storeId: storeInfo?.store_id,
        paymentOption: cartProps?.selectedTender,
        storeConfig: storeInfoConfig,
      };
      await routeOfferThroughEngine(offerToApply, addedItems, engineCtx, dispatch);
    }
  };

  const isAutoApplyOffer = (o) => {
    const termStyle = o?.offer_header?.term_style;
    if (termStyle === "C3") return true;
    if (termStyle === "C1") {
      return o?.offer_term?.[0]?.offering_type === "Item";
    }
    return false;
  };

  const getEffectiveDiscountValue = (o) => {
    const termStyle = o?.offer_header?.term_style;
    const terms = o?.offer_term || [];
    const termProducts = o?.offer_term_product || [];

    const term = termStyle === "C3"
      ? terms.find(t => parseFloat(t.discount_type_value) > 0)
      : terms[0];

    if (!term) return 0;

    const value = parseFloat(term.discount_type_value || 0);
    const disType = term.discount_type_code;
    const noPerTrans = String(term.no_pertrans ?? "1");

    let qualifyingItems = addedItems || [];
    if (term.is_include_items) {
      const termProds = termProducts.filter(p => p.offer_term_id === term.offer_term_id);
      const isAnyItemIdEmpty = termProds.some(p => p.item_id === "");
      if (isAnyItemIdEmpty) {
        const catIds = termProds.map(p => p.category_id);
        qualifyingItems = (addedItems || []).filter(i => catIds.includes(i.category_id));
      } else {
        const skus = termProds.map(p => String(p.item_id));
        qualifyingItems = (addedItems || []).filter(i => skus.includes(String(i.item_variant?.[0]?.sku)));
      }
    }

    if (!qualifyingItems.length) return 0;

    const sortedPlus = qualifyingItems
      .map(item => parseFloat(item.item_variant?.[0]?.plu || 0))
      .sort((a, b) => a - b);
    const cheapestPlu = sortedPlus[0] ?? 0;

    if (noPerTrans === "0") {
      if (disType === "2") return (value / 100) * cheapestPlu;
      return Math.min(value, cheapestPlu);
    }

    if (disType === "2") return (value / 100) * (displayItemTotal || 0);
    return value;
  };

  // Auto-apply best offer when storeOffers/addedItems change. Mirrors
  // CartOffers.js [offer1, userRemovedOffer] effect — dispatches directly
  // (not via handleApplyOffer, which toggles), and clears if engine returns
  // zero discount.
  useEffect(() => {
    if (!storeOffers?.length || !addedItems?.length) return;

    const activeOffer = cartProps?.appliedOffer;
    const eligibleOffers = storeOffers.filter(o => isAutoApplyOffer(o) && !isOfferDisabled(o));

    const bestAutoOffer = eligibleOffers.length
      ? eligibleOffers.reduce((best, o) =>
          getEffectiveDiscountValue(o) > getEffectiveDiscountValue(best) ? o : best
        , eligibleOffers[0])
      : null;

    const currentIsManual = activeOffer?.offer_id
      && !eligibleOffers.some(o => o.offer_id === activeOffer.offer_id);

    const engineDiscount = cartProps?.engineDiscount;
    const sameOfferAlreadyStamped = bestAutoOffer
      && activeOffer?.offer_id === bestAutoOffer.offer_id
      && engineDiscount?.total_discount > 0;

    if (bestAutoOffer && !currentIsManual && !sameOfferAlreadyStamped) {
      const termStyle = bestAutoOffer.offer_header?.term_style;
      const isSwitchingOffer = activeOffer?.offer_id && activeOffer.offer_id !== bestAutoOffer.offer_id;
      (async () => {
        if (isSwitchingOffer) {
          dispatch(removeDiscount());
          dispatch(clear_applied_offer());
          dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        }
        dispatch(applied_offer(bestAutoOffer));
        dispatch(addDiscount(bestAutoOffer.offer_term, termStyle));
        const engineCtx = {
          orderType: orderType?.order?.order_type || "Dine In",
          storeId: storeInfo?.store_id,
          paymentOption: cartProps?.selectedTender,
          storeConfig: storeInfoConfig,
        };
        const result = await routeOfferThroughEngine(bestAutoOffer, addedItems, { ...engineCtx, entryPoint: "auto" }, dispatch);
        if (!result || !(result.total_discount > 0)) {
          dispatch(removeDiscount());
          dispatch(clear_applied_offer());
          dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        }
      })();
    } else if (!bestAutoOffer && isAutoApplyOffer(activeOffer)) {
      dispatch(removeDiscount());
      dispatch(clear_applied_offer());
      dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeOffers]);

  // Re-evaluate applied offer whenever addedItems changes (qty +/-). Mirrors
  // CartOffers.js — that component isn't mounted in the desktop tree, so
  // without this the offer snapshot goes stale on qty change.
  useEffect(() => {
    const activeOffer = cartProps?.appliedOffer;
    if (!activeOffer || !addedItems?.length) return;
    routeOfferThroughEngine(
      activeOffer,
      addedItems,
      {
        orderType: cartProps?.orderType?.order?.order_type || orderType?.order?.order_type || "Dine In",
        storeId: storeInfo?.store_id,
        storeConfig: storeInfoConfig,
        paymentOption: cartProps?.selectedTender,
        tip: cartProps?.tip || 0,
        entryPoint: "auto",
      },
      dispatch
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedItems]);

  const cartQty = useMemo(() => {
    return (addedItems || [])
      .filter((item) => !item.is_void && !item.isVoid && !item.is_refund)
      .reduce((acc, currentItem) => acc + (currentItem.quantity || 0), 0);
  }, [addedItems]);

  const cartTotal = displayItemTotal || 0;
  const addedItemCategories = useMemo(() => (addedItems || []).map((e) => e.category_id), [addedItems]);
  const addedItemSkus = useMemo(() => (addedItems || []).map((e) => e.item_variant?.[0]?.sku), [addedItems]);

  const isOfferDisabled = (offer) => {
    if (offer?.disabled === true || offer?.disable === true) return true;
    const terms = offer?.offer_term || [];
    if (!terms.length) return false;
    const termStyle = offer?.offer_header?.term_style;
    const allTermProducts = offer?.offer_term_product || [];

    const checkItemConditions = (term) => {
      if (term?.is_include_items === false) return false;
      const termProducts = allTermProducts.filter(p => p.offer_term_id === term.offer_term_id);
      const disCategory = termProducts.map(e => e.category_id);
      const disItem = termProducts.map(e => e.item_id);
      const isAnyItemIdEmpty = disItem.some(id => id === "");

      if (!disCategory.some(categoryId => addedItemCategories.includes(categoryId))) return true;
      if (isAnyItemIdEmpty) return false;
      if (!disItem.some(itemId => addedItemSkus.includes(itemId))) return true;
      return false;
    };

    const isTermDisabled = (term) => {
      const disCount = term?.count ?? "1";
      const disAmount = term?.amount ?? "0";
      const conditionType = term?.condition_type?.toLowerCase();

      const isCountValid = Number(disCount) <= cartQty;
      const isAmountValid = parseFloat(disAmount) <= cartTotal;

      const skipItemCheck = term?.offering_type === "Transaction" || termStyle === "C2";

      if (conditionType === "both") {

        if (!isCountValid || !isAmountValid) return true;
        return skipItemCheck ? false : checkItemConditions(term);
      }

      if (conditionType === "count") {
        if (!isCountValid) return true;
        return skipItemCheck ? false : checkItemConditions(term);
      }

      if (conditionType === "amount") {
        if (!isAmountValid) return true;
        return skipItemCheck ? false : checkItemConditions(term);
      }

      return false;
    };

    if (termStyle === "C3") {
      const anyTermFails = terms.some(isTermDisabled);
      if (anyTermFails) return true;
      const buyTerm = terms.find(t => parseFloat(t.discount_type_value) === 0);
      const getTerm = terms.find(t => parseFloat(t.discount_type_value) > 0);
      const buyCount = parseInt(buyTerm?.count ?? 1);
      const getCount = parseInt(getTerm?.count ?? 1);
      if (cartQty < buyCount + getCount) return true;
      return false;
    }

    return isTermDisabled(terms[0]);
  };

  const handleApplyCoupon = async (codeToApply) => {
    setCouponError("");
    const code = (codeToApply || couponInputText).trim().toLowerCase();
    if (!code) return;

    const matched = storeOffers.find((o) =>
      (o?.offer_term || []).some(
        (t) => (t?.coupon_code || "").toLowerCase() === code
      ) || (o?.promo_code || "").toLowerCase() === code
      || (o?.code || "").toLowerCase() === code
      || (o?.title || "").toLowerCase() === code
      || (o?.offer_name || "").toLowerCase() === code
    );

    if (!matched) {
      setCouponError("Invalid coupon code");
      return;
    }
    if (isOfferDisabled(matched)) {
      setCouponError("Cart conditions for this coupon are not satisfied");
      return;
    }

    dispatch(removeDiscount());
    dispatch(clear_applied_offer());
    dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });

    const termStyle = matched.offer_header?.term_style;
    dispatch(applied_offer(matched));
    dispatch(addDiscount(matched.offer_term, termStyle));

    const engineCtx = {
      orderType: orderType?.order?.order_type || "Dine In",
      storeId: storeInfo?.store_id,
      paymentOption: cartProps?.selectedTender,
      storeConfig: storeInfoConfig,
      entryPoint: "coupon",
    };
    const result = await routeOfferThroughEngine(matched, addedItems, engineCtx, dispatch);
    if (!result || !(result.total_discount > 0)) {
      dispatch(removeDiscount());
      dispatch(clear_applied_offer());
      dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
      setCouponError("Cart conditions for this coupon are not satisfied");
      return;
    }
    setCouponInputText("");
  };

  // Capture cart state exactly once on mount to exclude items already in the cart (added from menu)
  const initialCartSkus = useRef(new Set(
    (props.cartProps?.addedItems || []).map(item => item?.sku || item?.item_variant?.[0]?.sku)
  ));

  // Suggested Items / Craving More items
  const cravingItems = useMemo(() => {
    const catalog = props.catalogItems || props.allitem || [];
    if (!catalog.length) return [];

    const filtered = catalog.filter(item => {
      if (item?.is_stock === false) return false;
      const sku = item?.sku || item?.item_variant?.[0]?.sku;
      return !initialCartSkus.current.has(sku);
    });

    return filtered.slice(0, 6);
  }, [props.catalogItems, props.allitem]);

  const handleAddItemToCart = (item) => {
    if (props.addItemToCart) {
      props.addItemToCart(item);
    } else {
      dispatch(addQuantity(item));
    }
  };

  return (
    <div style={{ backgroundColor: "rgb(255, 255, 255)", minHeight: "100vh", padding: "24px 0" }}>
      <div style={{ maxWidth: "100%", margin: "0 auto", padding: "0 24px" }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN: Header, Items, Craving More, Savings Corner */}
          <Grid item xs={12} md={8}>
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

              {/* 1. Header Card with Back Arrow & Order Type Dropdown */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "6px",
                  border: "1px solid #EAEAEA",
                  padding: "16px 10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    onClick={() => navigate("/menu")}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M20 12H4M4 12L10 6M4 12L10 18" stroke={primaryColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <Typography
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "20px",
                      fontWeight: "700",
                      color: "#1A1A1A",
                    }}
                  >
                    {storeInfo?.store_name}
                  </Typography>
                </div>

                {/* Right: Order Type Selector */}
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: "18px", fontWeight: "600", color: "#666666", display: "block", fontFamily: "Satoshi, sans-serif" }}>
                    Order Type
                  </span>
                  <div
                    onClick={(e) => !is_fsr && setOrderTypeAnchor(e.currentTarget)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "4px",
                      cursor: is_fsr ? "default" : "pointer",
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "18px",
                      fontWeight: "700",
                      color: primaryColor,
                    }}
                  >
                    {activeOrderTypeName}
                    {!is_fsr && <KeyboardArrowDownIcon style={{ fontSize: "26px", color: primaryColor }} />}
                  </div>
                  {!is_fsr && (
                    <Menu
                      anchorEl={orderTypeAnchor}
                      open={Boolean(orderTypeAnchor)}
                      onClose={() => setOrderTypeAnchor(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      getContentAnchorEl={null}
                      PaperProps={{
                        style: {
                          marginTop: "8px",
                          borderRadius: "8px",
                          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                          minWidth: "140px",
                        },
                      }}
                    >
                      {storeOrderTypes.map((type, idx) => {
                        const isSelected = (orderType?.order?.order_code || "").toLowerCase() === (type?.order_code || "").toLowerCase();
                        return (
                          <MenuItem
                            key={idx}
                            onClick={() => {
                              setOrderTypeAnchor(null);
                              if (isSelected) return;
                              setPendingOrderType(type);
                              setOpenOrderTypePopup(true);
                            }}
                          >
                            {type.order_type}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  )}
                </div>
              </div>

              {/* Order Type Confirmation Popup */}
              <OrderTypePopup
                open={openOrderTypePopup}
                onClose={() => {
                  setOpenOrderTypePopup(false);
                  setPendingOrderType(null);
                }}
                onConfirm={() => {
                  if (pendingOrderType) {
                    dispatch(set_order_type({ order: pendingOrderType }));
                    const code = (
                      pendingOrderType?.order_code ||
                      pendingOrderType?.order_type ||
                      ""
                    ).toLowerCase();
                    if (code === "del" || code.includes("delivery")) {
                      if (props.setOpenUserAddInfo) {
                        props.setOpenUserAddInfo(true);
                      }
                    } else if (code === "di" || code === "dine in" || code.includes("dine")) {
                      const isTentEnabled = storeInfoConfig?.web_ordering_setting?.dining_preference_qr === "Tent No.";
                      const location_id = sessionReducer?.location_id;
                      if (isTentEnabled && !location_id && !is_fsr) {
                        setOpenTent(true);
                      }
                    } else {
                      if (sessionReducer?.tent_id) {
                        dispatch({
                          type: 'WEB_LOGIN',
                          payload: {
                            ...sessionReducer,
                            tent_id: null
                          }
                        });
                      }
                    }
                  }
                  setOpenOrderTypePopup(false);
                  setPendingOrderType(null);
                }}
                orderType={pendingOrderType}
                storeInfo={storeInfo}
                storeInfoConfig={storeInfoConfig}
              />

              <AddressPopup
                openUserAddInfo={props.openUserAddInfo}
                setOpenUserAddInfo={props.setOpenUserAddInfo}
                storeInfo={storeInfo}
                storeInfoConfig={storeInfoConfig}
              />

              <TentPopup
                openTent={openTent}
                setOpenTent={setOpenTent}
                storeInfoConfig={storeInfoConfig}
              />

              {/* 2. Items Added Card */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>


                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #EAEAEA",
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "18px",
                      fontWeight: "500",
                      color: "#8C8C8C",
                      textAlign: "left",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    Items Added
                  </span>
                  {(() => {
                    const getMergeKey = (it) => {
                      const skus = [];
                      if (it?.item_variant?.[0]?.sku) skus.push(it.item_variant[0].sku);
                      const addons = it?.selected_addons_items || it?.selectedAddonsItems || [];
                      addons.forEach((a) => a?.sku && skus.push(a.sku));
                      skus.sort();
                      const modKey = JSON.stringify(it?.selected_modifier || it?.selectedModifier || []);
                      return `${it?._id || ""}|${it?.variant_name || ""}|${skus.join(",")}|${modKey}|${it?.specialRequest || ""}`;
                    };
                    const visibleItems = addedItems.filter((i) => !i.is_void && !i.isVoid && !i.is_refund);
                    const groupMap = new Map();
                    visibleItems.forEach((it) => {
                      const key = getMergeKey(it);
                      if (!groupMap.has(key)) groupMap.set(key, { representative: it, lines: [] });
                      groupMap.get(key).lines.push(it);
                    });
                    const mergedItems = Array.from(groupMap.values()).map((g) => {
                      const totalQty = g.lines.reduce((a, l) => a + (l.quantity || 0), 0);
                      const totalSubTotal = g.lines.reduce((a, l) => a + (l.sub_total || 0), 0);
                      const fullPriceLine = g.lines.find((l) => !(l.discount_amount || l.total_discount || 0)) || g.lines[0];
                      return {
                        ...g.representative,
                        quantity: totalQty,
                        sub_total: totalSubTotal,
                        _merged: { lines: g.lines, fullPriceLine },
                      };
                    });
                    return mergedItems.map((item, idx) => {
                    const itemPrice = parseFloat(item?.sub_total || 0);

                    const itemDiscountInfo = (() => {
                      const engineDiscount = cartProps?.engineDiscount;
                      const origPrice = itemPrice;
                      if (!engineDiscount || !(engineDiscount.total_discount > 0)) {
                        return { discount: 0, origPrice, finalPrice: origPrice, isFree: false, isDiscounted: false };
                      }
                      const engineItems = (engineDiscount.item_details || []).filter((ei) => !ei.main_item_id);
                      const itemSku = String(item?.item_variant?.[0]?.sku || item?.sku || "");
                      const itemId = String(item?._id || item?.item_id || item?.id || "");
                      const itemVariant = String(item?.variant_name || "");
                      const addonSig = (list) => (list || [])
                        .map((a) => String(a?._id || a?.sku || a?.item_name || ""))
                        .sort()
                        .join(",");
                      const displayAddonSig = addonSig(item?.selectedAddonsItems || item?.selected_addons_items);
                      const matches = engineItems.filter((ei) => {
                        const eiId = String(ei._id || ei.item_id || "");
                        const eiSku = String(ei.sku || ei.item_variant?.[0]?.sku || "");
                        const eiVariant = String(ei.variant_name || "");
                        const idMatch = itemId && eiId && itemId === eiId;
                        const skuMatch = itemSku && eiSku && itemSku === eiSku;
                        if (!(idMatch || skuMatch)) return false;
                        if (itemVariant && eiVariant && itemVariant !== eiVariant) return false;
                        if (addonSig(ei?.selectedAddonsItems || ei?.selected_addons_items) !== displayAddonSig) return false;
                        return true;
                      });
                      const discount = matches.reduce(
                        (a, m) => a + parseFloat(m?.discount_amount || m?.total_discount || 0),
                        0
                      );
                      const finalPrice = Math.max(0, origPrice - discount);
                      const isFree = discount > 0 && finalPrice === 0;
                      return {
                        discount,
                        origPrice,
                        finalPrice,
                        isFree,
                        isDiscounted: discount > 0,
                      };
                    })();

                    const editableLine = item?._merged?.fullPriceLine || item;

                    const addonNames = [];
                    const variantName = item?.item_variant?.[0]?.variant_name || item?.selectedVariantName;
                    if (variantName && variantName.toLowerCase() !== "regular") {
                      addonNames.push(variantName);
                    }

                    const selectedMods = item?.selected_modifier || item?.selectedModifier || [];
                    selectedMods.forEach((group) => {
                      if (Array.isArray(group?.modifiers)) {
                        group.modifiers.forEach((m) => {
                          const name = m?.modifier_name || m?.name;
                          if (name) addonNames.push(name);
                        });
                      } else if (group?.modifier_name || group?.name) {
                        addonNames.push(group?.modifier_name || group?.name);
                      }
                    });

                    const addonsList = Array.isArray(item?.selectedAddonsItems)
                      ? item.selectedAddonsItems
                      : Array.isArray(item?.selected_addons_items)
                        ? item.selected_addons_items
                        : Array.isArray(item?.subItems)
                          ? item.subItems
                          : [];

                    addonsList.forEach((e) => {
                      const iName = (e?.item_name || e?.name || e?.item_variant?.[0]?.item_name || "").replace(/\s*Regular\b/gi, "").trim();
                      if (iName) {
                        addonNames.push(iName);
                      }
                    });
                    const allAddonsText = addonNames.join(", ");

                    return (
                      <div
                        key={`${item._id || item.id}-${idx}`}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          width: "100%",
                        }}
                      >
                        {/* Top Row: Name + Stepper + Price */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0, paddingRight: "12px" }}>
                            <div style={{ width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {(() => {
                                const d = item?.dietary?.toLowerCase()?.trim();
                                const isVeg = d === "veg";
                                const isNonVegOrEgg = d === "non-veg" || d === "nonveg" || d === "non veg" || d === "non_veg" || d === "egg" || d === "eggetarian" || d === "eggiterian" || d === "eggitarian";
                                if (!isVeg && !isNonVegOrEgg) return null;
                                return (
                                  <img
                                    src={isVeg ? VegIcon : NonVegIcon}
                                    alt={item?.dietary || "dietary"}
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                );
                              })()}
                            </div>
                            <span
                              style={{
                                fontFamily: "Satoshi, sans-serif",
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#1A1A1A",
                                wordBreak: "break-word",
                              }}
                            >
                              {item.item_name}
                            </span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: primaryColor,
                                borderRadius: "4px",
                                padding: "2px 6px",
                                width: "68px",
                                height: "28px",
                                boxSizing: "border-box",
                                flexShrink: 0,
                              }}
                            >
                              <RemoveIcon
                                style={{ color: "#ffffff", fontSize: "18px", cursor: "pointer" }}
                                onClick={() => dispatch(subtractQuantity(editableLine))}
                              />
                              <span
                                style={{
                                  color: "#ffffff",
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  fontFamily: "Satoshi, sans-serif",
                                  minWidth: "18px",
                                  textAlign: "center",
                                  display: "inline-block",
                                }}
                              >
                                {item.quantity}
                              </span>
                              <AddIcon
                                style={{ color: "#ffffff", fontSize: "18px", cursor: "pointer" }}
                                onClick={() => dispatch(addQuantity(editableLine))}
                              />
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: "95px", flexShrink: 0 }}>
                              {itemDiscountInfo.isDiscounted && (
                                <span
                                  style={{
                                    fontFamily: "Satoshi, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#999999",
                                    textDecoration: "line-through",
                                    lineHeight: "1.1",
                                  }}
                                >
                                  {currency}{itemDiscountInfo.origPrice.toFixed(2)}
                                </span>
                              )}
                              <span
                                style={{
                                  fontFamily: "Satoshi, sans-serif",
                                  fontSize: "20px",
                                  fontWeight: "700",
                                  color: itemDiscountInfo.isFree ? "#2E7D32" : (itemDiscountInfo.isDiscounted ? primaryColor : "#1A1A1A"),
                                  textAlign: "right",
                                  display: "inline-block",
                                }}
                              >
                                {itemDiscountInfo.isFree ? (
                                  "FREE"
                                ) : (
                                  `${currency}${itemDiscountInfo.finalPrice % 1 === 0 ? itemDiscountInfo.finalPrice.toFixed(0) : itemDiscountInfo.finalPrice.toFixed(2)}`
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Free / Discount Badge Row */}
                        {itemDiscountInfo.isDiscounted && (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "24px", marginTop: "2px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                backgroundColor: itemDiscountInfo.isFree ? "#E8F5E9" : `${primaryColor}15`,
                                color: itemDiscountInfo.isFree ? "#2E7D32" : primaryColor,
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "12px",
                                fontWeight: "700",
                                fontFamily: "Satoshi, sans-serif",
                              }}
                            >
                              {itemDiscountInfo.isFree ? (
                                <>🎉 FREE with Coupon</>
                              ) : (
                                <>🏷️ Discount Applied</>
                              )}
                            </span>
                          </div>
                        )}

                        {/* Bottom Row: Variant & Addons Info */}
                        {allAddonsText ? (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "24px", marginTop: "2px" }}>
                            <span
                              style={{
                                fontFamily: "Satoshi, sans-serif",
                                fontSize: "14px",
                                color: "#777777",
                                textAlign: "left",
                                lineHeight: "1.3",
                              }}
                            >
                              {allAddonsText}
                            </span>
                          </div>
                        ) : null}
                        <div
                          onClick={() => handleOpenItemNote(item)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginLeft: "24px",
                            marginTop: "6px",
                            cursor: "pointer",
                            userSelect: "none",
                            width: "fit-content",
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                            <path
                              d="M13.7682 1.962L12.0116 0.22213C11.8606 0.0795438 11.66 0 11.4513 0C11.2427 0 11.0421 0.0795438 10.8911 0.22213L9.32914 1.7739H0.94953C0.697699 1.7739 0.456182 1.87299 0.278111 2.04936C0.100039 2.22573 0 2.46494 0 2.71437V13.0595C0 13.309 0.100039 13.5482 0.278111 13.7245C0.456182 13.9009 11.8877 13.9009 12.0658 13.7245C12.2439 13.5482 12.3439 13.309 12.3439 13.0595V4.48245L13.7682 3.07175C13.9166 2.92451 14 2.72495 14 2.51687C14 2.3088 13.9166 2.10924 13.7682 1.962ZM7.04077 8.41832L5.0515 8.85563L5.52627 6.90416L10.0603 2.40402L11.5938 3.92287L7.04077 8.41832ZM12.1065 3.38681L10.573 1.86795L11.4513 0.998017L12.9848 2.51687L12.1065 3.38681Z"
                              fill={primaryColor || "#3159A6"}
                            />
                          </svg>
                          <span
                            style={{
                              fontFamily: "Satoshi, sans-serif",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: (item?.specialRequest || item?.special_request || item?.instruction) ? "#333333" : "#888888",
                              wordBreak: "break-word",
                              textAlign: "left",
                              lineHeight: "1.4",
                            }}
                          >
                            {(item?.specialRequest || item?.special_request || item?.instruction)
                              ? (item?.specialRequest || item?.special_request || item?.instruction)
                              : "Special Request"}
                          </span>
                        </div>
                      </div>
                    );
                    });
                  })()}

                  <div style={{ height: "1px", backgroundColor: "#F2F2F7", margin: "8px 0" }} />

                  {/* Action Buttons Row */}
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/menu")}
                      style={{
                        borderColor: primaryColor,
                        color: primaryColor,
                        borderRadius: "8px",
                        fontFamily: "Satoshi, sans-serif",
                        fontWeight: "600",
                        fontSize: "18px",
                        textTransform: "none",
                        padding: "4px 14px",
                      }}
                      startIcon={<AddIcon style={{ color: primaryColor, fontSize: "28px", fontWeight: "700" }} />}
                    >
                      Add More Items
                    </Button>
                    {orderLevelNote ? (
                      <div
                        onClick={() => {
                          setInstructionsText(orderLevelNote);
                          setOpenInstructionsDialog(true);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          border: `1px solid ${primaryColor}`,
                          backgroundColor: `${primaryColor}0A`,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                          <path
                            d="M13.7682 1.962L12.0116 0.22213C11.8606 0.0795438 11.66 0 11.4513 0C11.2427 0 11.0421 0.0795438 10.8911 0.22213L9.32914 1.7739H0.94953C0.697699 1.7739 0.456182 1.87299 0.278111 2.04936C0.100039 2.22573 0 2.46494 0 2.71437V13.0595C0 13.309 0.100039 13.5482 0.278111 13.7245C0.456182 13.9009 0.697699 14 0.94953 14H11.3944C11.6462 14 11.8877 13.9009 12.0658 13.7245C12.2439 13.5482 12.3439 13.309 12.3439 13.0595V4.48245L13.7682 3.07175C13.9166 2.92451 14 2.72495 14 2.51687C14 2.3088 13.9166 2.10924 13.7682 1.962ZM7.04077 8.41832L5.0515 8.85563L5.52627 6.90416L10.0603 2.40402L11.5938 3.92287L7.04077 8.41832ZM12.1065 3.38681L10.573 1.86795L11.4513 0.998017L12.9848 2.51687L12.1065 3.38681Z"
                            fill={primaryColor}
                          />
                        </svg>
                        <div style={{ display: "flex", flexDirection: "column", textAlign: "left", minWidth: 0, overflow: "hidden" }}>
                          <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "13px", fontWeight: "700", color: primaryColor }}>
                            Note to Kitchen:
                          </span>
                          <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "14px", fontWeight: "500", color: "#333333", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            {orderLevelNote}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => setOpenInstructionsDialog(true)}
                        style={{
                          borderColor: primaryColor,
                          color: primaryColor,
                          borderRadius: "8px",
                          fontFamily: "Satoshi, sans-serif",
                          fontWeight: "600",
                          fontSize: "15px",
                          textTransform: "none",
                          padding: "4px 14px",
                        }}
                        startIcon={
                          <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M13.7682 1.962L12.0116 0.22213C11.8606 0.0795438 11.66 0 11.4513 0C11.2427 0 11.0421 0.0795438 10.8911 0.22213L9.32914 1.7739H0.94953C0.697699 1.7739 0.456182 1.87299 0.278111 2.04936C0.100039 2.22573 0 2.46494 0 2.71437V13.0595C0 13.309 0.100039 13.5482 0.278111 13.7245C0.456182 13.9009 0.697699 14 0.94953 14H11.3944C11.6462 14 11.8877 13.9009 12.0658 13.7245C12.2439 13.5482 12.3439 13.309 12.3439 13.0595V4.48245L13.7682 3.07175C13.9166 2.92451 14 2.72495 14 2.51687C14 2.3088 13.9166 2.10924 13.7682 1.962ZM7.04077 8.41832L5.0515 8.85563L5.52627 6.90416L10.0603 2.40402L11.5938 3.92287L7.04077 8.41832ZM12.1065 3.38681L10.573 1.86795L11.4513 0.998017L12.9848 2.51687L12.1065 3.38681Z"
                              fill={primaryColor}
                            />
                          </svg>
                        }
                      >
                        Note to Kitchen
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. CRAVING MORE? Section */}
              {cravingItems.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
                  <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
                  <span
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "18px",
                      fontWeight: "900",
                      color: "#1A1A1A",
                      letterSpacing: "0.5px",
                      textAlign: "left",
                    }}
                  >
                    CRAVING MORE?
                  </span>

                  <div
                    className="no-scrollbar"
                    style={{
                      display: "flex",
                      gap: "14px",
                      overflowX: "auto",
                      paddingBottom: "4px",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    {cravingItems.map((item, idx) => {
                      const itemPrice = item?.item_variant?.length ? (() => {
                        const p = parseFloat(item.item_variant[0].plu);
                        return p % 1 === 0 ? p.toFixed(0) : p.toFixed(2);
                      })() : "150";

                      const itemId = item._id || item.id || item.item_id;
                      const itemSku = item?.sku || item?.item_variant?.[0]?.sku;
                      const cartItems = addedItems?.filter((e) => {
                        const eId = e._id || e.id || e.item_id;
                        const eSku = e?.sku || e?.item_variant?.[0]?.sku;
                        return (itemId && eId && itemId === eId) || (itemSku && eSku && String(itemSku) === String(eSku));
                      }) || [];
                      const cartQty = cartItems.reduce((acc, curr) => acc + (curr.quantity || 0), 0);
                      const primaryCartItem = cartItems[0];

                      return (
                        <div
                          key={idx}
                          style={{
                            minWidth: "165px",
                            maxWidth: "165px",
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            border: "1px solid #E5E5EA",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                          }}
                        >
                          <img
                            src={item?.item_display_img || defaultimg}
                            alt={item.item_name}
                            style={{ width: "100%", height: "115px", objectFit: "cover" }}
                            onError={(e) => { e.target.onerror = null; e.target.src = defaultimg; }}
                          />
                          <div style={{ padding: "10px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              {(() => {
                                const d = item?.dietary?.toLowerCase()?.trim();
                                const isVeg = d === "veg";
                                const isNonVegOrEgg = d === "non-veg" || d === "nonveg" || d === "non veg" || d === "non_veg" || d === "egg" || d === "eggetarian" || d === "eggiterian" || d === "eggitarian";
                                if (!isVeg && !isNonVegOrEgg) return null;
                                return (
                                  <img
                                    src={isVeg ? VegIcon : NonVegIcon}
                                    alt="dietary"
                                    style={{ width: "14px", height: "14px", flexShrink: 0 }}
                                  />
                                );
                              })()}
                              <span
                                style={{
                                  fontFamily: "Satoshi, sans-serif",
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "#1A1A1A",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item.item_name}
                              </span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
                              <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "15px", fontWeight: "700", color: "#1A1A1A" }}>
                                {currency}{itemPrice}
                              </span>
                              {cartQty > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    backgroundColor: primaryColor,
                                    borderRadius: "3px",
                                    padding: "2px 6px",
                                    minWidth: "64px",
                                    height: "28px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <RemoveIcon
                                    style={{ color: "#ffffff", fontSize: "16px", cursor: "pointer" }}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (primaryCartItem) {
                                        dispatch(subtractQuantity(primaryCartItem));
                                      }
                                    }}
                                  />
                                  <span
                                    style={{
                                      color: "#ffffff",
                                      fontSize: "14px",
                                      fontWeight: "700",
                                      fontFamily: "Satoshi, sans-serif",
                                    }}
                                  >
                                    {cartQty}
                                  </span>
                                  <AddIcon
                                    style={{ color: "#ffffff", fontSize: "16px", cursor: "pointer" }}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      if (primaryCartItem) {
                                        dispatch(addQuantity(primaryCartItem));
                                      } else {
                                        handleAddItemToCart(item);
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <Button
                                  size="small"
                                  onClick={() => handleAddItemToCart(item)}
                                  style={{
                                    border: `2px solid ${primaryColor}`,
                                    color: primaryColor,
                                    borderRadius: "3px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    padding: "4px 18px",
                                    minWidth: "60px",
                                    height: "28px",
                                    textTransform: "none",
                                    backgroundColor: "#ffffff",
                                    boxShadow: "none",
                                  }}
                                >
                                  Add
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 4. Savings Corner */}
              {(storeOffers.length > 0 || Boolean(customerReducer?.customer?.cust_mobile && customerReducer?.customer?.guest !== "Guest" && (customerReducer?.customer?.cust_point || customerReducer?.customer?.points) > 0)) && (
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #EAEAEA",
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#8C8C8C",
                      marginBottom: "4px",
                      textAlign: "left",
                      display: "block",
                      width: "100%",
                    }}
                  >
                    Savings Corner
                  </span>

                  {/* Coupon Code Input */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <TextField
                        placeholder="Enter Coupon / Promo Code"
                        value={couponInputText}
                        onChange={(e) => {
                          setCouponInputText(e.target.value);
                          if (couponError) setCouponError("");
                        }}
                        size="small"
                        variant="outlined"
                        fullWidth
                        style={{ backgroundColor: "#FAFAFA" }}
                        InputProps={{
                          endAdornment: couponInputText ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCouponInputText("");
                                  if (couponError) setCouponError("");
                                }}
                                style={{ padding: "4px" }}
                              >
                                <CloseIcon style={{ fontSize: "18px", color: "#888888" }} />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleApplyCoupon();
                        }}
                      />
                      <Button
                        onClick={() => handleApplyCoupon()}
                        style={{
                          backgroundColor: primaryColor,
                          color: "#ffffff",
                          fontWeight: "700",
                          fontFamily: "Satoshi, sans-serif",
                          borderRadius: "8px",
                          padding: "8px 20px",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Apply Code
                      </Button>
                    </div>
                    {couponError && (
                      <Typography style={{ color: "#E53935", fontSize: "12px", fontFamily: "Satoshi, sans-serif" }}>
                        {couponError}
                      </Typography>
                    )}
                  </div>

                  {visibleStoreOffers.length > 0 && <div style={{ height: "1px", backgroundColor: "#F2F2F7", margin: "4px 0" }} />}

                  {visibleStoreOffers.map((offer, idx) => {
                    const offerTitle = offer?.offer_header?.deal_type || offer?.deal_type || offer?.title || "";
                    const offerDesc = offer?.offer_name || offer?.description || "";
                    const promoCode = offer?.promo_code || offer?.code || offer?.offer_term?.[0]?.coupon_code || "";
                    const isDisabled = isOfferDisabled(offer);
                    const targetOfferId = getOfferId(offer);
                    const isThisOfferApplied = isOfferApplied && !isDisabled && Boolean(currentAppliedOfferId && targetOfferId && String(currentAppliedOfferId) === String(targetOfferId));

                    return (
                      <React.Fragment key={offer._id || offer.offer_id || idx}>
                        {idx > 0 && <div style={{ height: "1px", backgroundColor: "#F2F2F7", margin: "4px 0" }} />}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                backgroundColor: `${primaryColor}15`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={couponWebSvg}
                                alt="coupon"
                                style={{
                                  width: "20px",
                                  height: "18px",
                                  filter: primaryFilter,
                                }}
                              />
                            </div>
                            <div>
                              <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "15px", fontWeight: "700", color: "#1A1A1A" }}>
                                {offerTitle}
                              </Typography>
                              {/* <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "12px", color: "#888888" }}>
                                {offerDesc} {promoCode && <strong style={{ color: primaryColor, marginLeft: "4px" }}>PROMO CODE {promoCode}</strong>}
                              </Typography> */}
                            </div>
                          </div>
                          <Button
                            disabled={isDisabled}
                            onClick={() => handleApplyOffer(offer)}
                            style={{
                              backgroundColor: isThisOfferApplied ? primaryColor : "transparent",
                              border: `1.5px solid ${isThisOfferApplied ? primaryColor : "#E0E0E0"}`,
                              color: isThisOfferApplied ? "#ffffff" : (isDisabled ? "#AAAAAA" : primaryColor),
                              borderRadius: "8px",
                              fontFamily: "Satoshi, sans-serif",
                              fontWeight: "700",
                              fontSize: "13px",
                              textTransform: "none",
                              padding: "4px 18px",
                            }}
                          >
                            {isThisOfferApplied ? "Remove" : "Apply"}
                          </Button>
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Wallet Points Row (only when customer is logged in and has points in Redux) */}
                  {Boolean(customerReducer?.customer?.cust_mobile && customerReducer?.customer?.guest !== "Guest" && (customerReducer?.customer?.cust_point || customerReducer?.customer?.points) > 0) && (
                    <>
                      {storeOffers.length > 0 && <div style={{ height: "1px", backgroundColor: "#F2F2F7", margin: "4px 0" }} />}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div
                            style={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "50%",
                              backgroundColor: `${primaryColor}15`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <AccountBalanceWalletOutlinedIcon style={{ color: primaryColor, fontSize: "20px" }} />
                          </div>
                          <div>
                            <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "15px", fontWeight: "700", color: "#1A1A1A" }}>
                              Available {storeInfo?.store_name || "Burger"} Points: {customerReducer?.customer?.cust_point || customerReducer?.customer?.points || 0}
                            </Typography>
                            <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "12px", color: "#888888" }}>
                              Use Wallet Balance
                            </Typography>
                          </div>
                        </div>
                        <Button
                          style={{
                            backgroundColor: "transparent",
                            border: `1.5px solid ${primaryColor}`,
                            color: primaryColor,
                            borderRadius: "8px",
                            fontFamily: "Satoshi, sans-serif",
                            fontWeight: "700",
                            fontSize: "13px",
                            textTransform: "none",
                            padding: "4px 18px",
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </Grid>

          {/* RIGHT COLUMN: Bill Details & Pay Now Button */}
          <Grid item xs={12} md={4}>
            <div
              style={{
                position: "sticky",
                top: "80px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {/* Delivery Details Card (only when order type is Delivery) */}
              {isDelivery && (
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid #EAEAEA",
                    padding: "20px 24px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#8E8E93",
                      display: "block",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    Delivery Details
                  </span>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <LocationOnIcon style={{ color: primaryColor, fontSize: "24px" }} />
                      <div style={{ textAlign: "left" }}>
                        <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "15px", fontWeight: "700", color: "#1A1A1A" }}>
                          Delivery Address
                        </Typography>
                        <Typography style={{ fontFamily: "Satoshi, sans-serif", fontSize: "13px", color: selectedAddressText ? "#666666" : "#888888" }}>
                          {selectedAddressText || "No address selected"}
                        </Typography>
                      </div>
                    </div>

                    <span
                      onClick={() => props.setOpenUserAddInfo && props.setOpenUserAddInfo(true)}
                      style={{
                        fontFamily: "Satoshi, sans-serif",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: primaryColor,
                        cursor: "pointer",
                      }}
                    >
                      {selectedAddressText ? "Change" : "Add"}
                    </span>
                  </div>
                </div>
              )}

              {/* Bill Details Card */}
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "8px",
                  border: "1px solid #EAEAEA",
                  padding: "20px 24px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                <span
                  style={{
                    fontFamily: "Satoshi, sans-serif",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#8E8E93",
                    display: "block",
                    textAlign: "left",
                    width: "100%",
                  }}
                >
                  Bill Details
                </span>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", color: "#666666", fontWeight: "500" }}>
                      Item Total
                    </span>
                    <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", fontWeight: "600", color: "#333333" }}>
                      {currency}{(() => {
                        const p = parseFloat(displayItemTotal);
                        return p % 1 === 0 ? p.toFixed(0) : p.toFixed(2);
                      })()}
                    </span>
                  </div>

                  {packagingCharges > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", color: "#666666", fontWeight: "500" }}>
                        Packaging Charges
                      </span>
                      <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", fontWeight: "600", color: "#333333" }}>
                        {currency}{(() => {
                          const p = parseFloat(packagingCharges);
                          return p % 1 === 0 ? p.toFixed(0) : p.toFixed(2);
                        })()}
                      </span>
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", color: "#666666", fontWeight: "500" }}>
                      GST (Govt. Taxes)
                    </span>
                    <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "18px", fontWeight: "600", color: "#333333" }}>
                      {currency}{displayTax.toFixed(2)}
                    </span>
                  </div>

                  {discountAmount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "16px", color: primaryColor, fontWeight: "600" }}>
                        Discount Applied
                      </span>
                      <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "16px", fontWeight: "600", color: primaryColor }}>
                        - {currency}{discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Light horizontal divider */}
                <div style={{ height: "1px", backgroundColor: "#F2F2F7", margin: "4px 0" }} />

                {/* Grand Total Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "20px", fontWeight: "700", color: "#1A1A1A" }}>
                    Grand Total
                  </span>
                  <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "22px", fontWeight: "700", color: "#1A1A1A" }}>
                    {currency}{grandTotal.toFixed(2)}
                  </span>
                </div>

                {/* Opt-in Checkbox */}
                {/* <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "4px" }}>
                  <input
                    type="checkbox"
                    id="optInOffers"
                    checked={optInOffers}
                    onChange={(e) => setOptInOffers(e.target.checked)}
                    style={{
                      accentColor: primaryColor,
                      width: "18px",
                      height: "18px",
                      marginTop: "2px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  />
                  <label
                    htmlFor="optInOffers"
                    style={{
                      fontFamily: "Satoshi, sans-serif",
                      fontSize: "14px",
                      color: "#8C8C8C",
                      lineHeight: "1.4",
                      cursor: "pointer",
                      textAlign: "left",
                      display: "block",
                    }}
                  >
                    Yes, I would like to receive updates and exclusive offers from {storeInfo?.store_name || "The Burger Club"}
                  </label>
                </div> */}

                {/* Pay Button / Select Delivery Address Button */}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (isDelivery && !selectedAddressText) {
                      if (props.setOpenUserAddInfo) {
                        props.setOpenUserAddInfo(true);
                      }
                    } else {
                      props.onPlaceOrder();
                    }
                  }}
                  style={{
                    backgroundColor: primaryColor,
                    color: "#ffffff",
                    fontSize: "17px",
                    fontWeight: "700",
                    fontFamily: "Satoshi, sans-serif",
                    borderRadius: "6px",
                    padding: "8px 0",
                    textTransform: "none",
                    boxShadow: "none",
                    marginTop: "4px",
                  }}
                >
                  {isDelivery && !selectedAddressText ? "Select Delivery Address" : "Pay Now"}
                </Button>
              </div>

              {/* Security Footer */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", color: "#888888" }}>
                <LockIcon style={{ fontSize: "20px", color: primaryColor }} />
                <span style={{ fontFamily: "Satoshi, sans-serif", fontSize: "16px", fontWeight: "500" }}>
                  Safe & secure payments
                </span>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* Note to Kitchen Popup Dialog */}
      <Dialog
        open={openInstructionsDialog}
        onClose={() => setOpenInstructionsDialog(false)}
        PaperProps={{
          style: {
            borderRadius: "18px",
            border: "1px solid #e0e0e0",
            padding: "0px",
            maxWidth: "480px",
            width: "100%",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            fontFamily: "'Satoshi', sans-serif",
            backgroundColor: "#ffffff",
          }}
        >
          {/* Header with Title, Line Below, and Close Button */}
          <Box
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 24px",
              borderBottom: "1px solid #eaeaea",
              boxSizing: "border-box",
            }}
          >
            <Typography
              style={{
                fontWeight: 700,
                fontSize: "20px",
                color: primaryColor,
                fontFamily: "'Satoshi', sans-serif",
              }}
            >
              Note to kitchen
            </Typography>
            <IconButton
              onClick={() => setOpenInstructionsDialog(false)}
              size="small"
              disableRipple
              style={{
                color: primaryColor,
                padding: "4px",
                backgroundColor: "transparent",
              }}
            >
              <CloseIcon style={{ fontSize: "22px" }} />
            </IconButton>
          </Box>

          {/* Body Content */}
          <Box
            style={{
              width: "100%",
              padding: "24px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Input Field Container */}
            <Box
              style={{
                width: "100%",
                height: "54px",
                borderRadius: "8px",
                border: `1.5px solid ${primaryColor}`,
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                boxSizing: "border-box",
              }}
            >
              <input
                type="text"
                className="note-kitchen-input"
                placeholder="e.g. Dont make it too spicy"
                maxLength={500}
                value={instructionsText || ""}
                onChange={(e) => setInstructionsText(e.target.value.slice(0, 500))}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  fontFamily: "'Satoshi', sans-serif",
                  color: "#333333",
                  backgroundColor: "transparent",
                  height: "100%",
                  minWidth: 0,
                }}
              />
              {/* Store Color Pen SVG Icon */}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0, marginLeft: "12px" }}
              >
                <path
                  d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z"
                  fill={primaryColor}
                />
              </svg>
            </Box>
            <span style={{ fontSize: "12px", color: "#888888", textAlign: "right", marginTop: "4px", display: "block" }}>
              {(instructionsText || "").length}/500
            </span>

            {/* Space between Input Field and Continue Button */}
            <Box style={{ marginTop: "32px" }}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={handleSaveInstructions}
                style={{
                  height: "50px",
                  borderRadius: "10px",
                  backgroundColor: primaryColor,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "16px",
                  textTransform: "none",
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Item-level Note to Kitchen Popup Dialog */}
      <Dialog
        open={openItemNoteDialog}
        onClose={() => setOpenItemNoteDialog(false)}
        PaperProps={{
          style: {
            borderRadius: "18px",
            border: "1px solid #e0e0e0",
            padding: "0px",
            maxWidth: "480px",
            width: "100%",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            fontFamily: "'Satoshi', sans-serif",
            backgroundColor: "#ffffff",
          }}
        >
          {/* Header */}
          <Box
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 24px",
              borderBottom: "1px solid #eaeaea",
              boxSizing: "border-box",
            }}
          >
            <Box style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                style={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: primaryColor,
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                Note to kitchen
              </Typography>
              {editingCartItem?.item_name && (
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#666666",
                    fontFamily: "'Satoshi', sans-serif",
                    marginTop: "2px",
                  }}
                >
                  For {editingCartItem.item_name}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={() => setOpenItemNoteDialog(false)}
              size="small"
              disableRipple
              style={{
                color: primaryColor,
                padding: "4px",
                backgroundColor: "transparent",
              }}
            >
              <CloseIcon style={{ fontSize: "22px" }} />
            </IconButton>
          </Box>

          {/* Body Content */}
          <Box
            style={{
              width: "100%",
              padding: "24px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Input Field Container */}
            <Box
              style={{
                width: "100%",
                height: "54px",
                borderRadius: "8px",
                border: `1.5px solid ${primaryColor}`,
                backgroundColor: "#ffffff",
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                boxSizing: "border-box",
              }}
            >
              <input
                type="text"
                className="note-kitchen-input"
                placeholder="e.g. Dont make it too spicy"
                maxLength={500}
                value={itemNoteText || ""}
                onChange={(e) => setItemNoteText(e.target.value.slice(0, 500))}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "15px",
                  fontWeight: 500,
                  fontFamily: "'Satoshi', sans-serif",
                  color: "#333333",
                  backgroundColor: "transparent",
                  height: "100%",
                  minWidth: 0,
                }}
              />
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ flexShrink: 0, marginLeft: "12px" }}
              >
                <path
                  d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z"
                  fill={primaryColor}
                />
              </svg>
            </Box>
            <span style={{ fontSize: "12px", color: "#888888", textAlign: "right", marginTop: "4px", display: "block" }}>
              {(itemNoteText || "").length}/500
            </span>

            {/* Save Button */}
            <Box style={{ marginTop: "32px" }}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={handleSaveItemNote}
                style={{
                  height: "50px",
                  borderRadius: "10px",
                  backgroundColor: primaryColor,
                  color: "white",
                  fontWeight: 700,
                  fontSize: "16px",
                  textTransform: "none",
                  fontFamily: "'Satoshi', sans-serif",
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </div>
  );
}
